import { useTranslation } from "react-i18next";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { Button } from "../../../components/core/basic/Button";
import { UploadStatus } from "../../uploads/constants";
import type { Upload } from "../../../zodios/apiDefinitions/uploads";
import { downloadPricingPlanUploadCsv } from "./api/downloadPricingPlanUploadCsv";

export function CommitStep({ upload, onClose }: { upload: Upload; onClose: () => void }) {
  const { t } = useTranslation(["pricingv2"]);
  const total = (upload.valid_rows ?? 0) + (upload.warning_rows ?? 0);
  const committed = upload.committed_rows ?? 0;
  const failed = upload.failed_rows ?? 0;
  const processed = committed + failed;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isDone = upload.status === UploadStatus.DONE || upload.status === UploadStatus.PARTIAL;

  return (
    <VStack gap="large" alignItems="stretch">
      <HStack gap="xlarge">
        <CountTile label={t(($) => $.pricingv2.Committed)} value={committed} color="green.600" />
        <CountTile label={t(($) => $.pricingv2.Failed)} value={failed} color="red.600" />
        <CountTile label={t(($) => $.pricingv2.Pending)} value={Math.max(0, total - processed)} color="gray.600" />
      </HStack>

      <div
        className={css({
          height: "12px",
          width: "100%",
          backgroundColor: "gray.200",
          borderRadius: "full",
          overflow: "hidden",
        })}
      >
        <div
          className={css({
            height: "100%",
            backgroundColor: isDone && failed > 0 ? "yellow.500" : "green.500",
            transition: "width 0.3s",
          })}
          style={{ width: `${pct}%` }}
        />
      </div>

      {isDone && (
        <Flex justifyContent="end" gap="medium">
          {failed > 0 && (
            <Button variant="secondary" onPress={() => void downloadPricingPlanUploadCsv(upload.id, "result")}>
              {t(($) => $.pricingv2["Download result CSV"])}
            </Button>
          )}
          <Button onPress={onClose}>{t(($) => $.pricingv2.Close)}</Button>
        </Flex>
      )}
    </VStack>
  );
}

function CountTile({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <VStack alignItems="start" gap="xsmall">
      <span className={css({ fontSize: "small", color: "gray.600" })}>{label}</span>
      <span className={css({ fontSize: "xxxlarge", fontWeight: "bold", color })}>{value}</span>
    </VStack>
  );
}
